// Shared helpers for the public /race-results page.
// Race shape comes from /api/public-race-results (see api/_public-race-results.js).
import { formatTimeOfDay } from "@/utils/time-format";

export const TIMEZONE = "Australia/Sydney";
export const MAX_DAYS_BACK = 730;

// Customer-facing edition names, in the order a race's tips are published.
export const EDITIONS = [
  { key: "em", label: "Early Mail" },
  { key: "rd", label: "Race Day" },
  { key: "lm", label: "Late Mail" },
];

export const VIEWS = ["list", "winners", "meetings", "calendar"];

const pad2 = (n) => String(n).padStart(2, "0");

/** Today's date in Sydney as YYYY-MM-DD. */
export function sydneyTodayIso(now = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

export function addDaysIso(iso, days) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The newest day with results (yesterday in Sydney). */
export const latestResultsIso = (now = new Date()) =>
  addDaysIso(sydneyTodayIso(now), -1);

export const earliestResultsIso = (now = new Date()) =>
  addDaysIso(sydneyTodayIso(now), -MAX_DAYS_BACK);

/** `count` days ending at `endIso`, newest first. */
export function daysEndingAt(endIso, count) {
  return Array.from({ length: count }, (_, i) => addDaysIso(endIso, -i));
}

/** Every day of a month (1-based month) that has results, newest first. */
export function monthDays(year, month, latestIso, earliestIso) {
  const days = [];
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  for (let d = last; d >= 1; d -= 1) {
    const iso = `${year}-${pad2(month)}-${pad2(d)}`;
    if (iso <= latestIso && iso >= earliestIso) days.push(iso);
  }
  return days;
}

const isoToUtcDate = (iso) => new Date(`${iso}T00:00:00Z`);

export const formatDayLong = (iso) =>
  isoToUtcDate(iso).toLocaleDateString("en-AU", {
    timeZone: "UTC",
    weekday: "long",
    day: "numeric",
    month: "long",
  });

export const formatDayShort = (iso) =>
  isoToUtcDate(iso).toLocaleDateString("en-AU", {
    timeZone: "UTC",
    weekday: "short",
    day: "numeric",
    month: "short",
  });

/** "1:15 pm" in Sydney time, from an ISO start time (the site-wide format). */
export function formatRaceTime(startIso) {
  if (!startIso) return "";
  return formatTimeOfDay(startIso, { timeZone: TIMEZONE });
}

/** "AEDT" or "AEST" for a given day. */
export function sydneyZoneLabel(iso) {
  const part = new Intl.DateTimeFormat("en-AU", {
    timeZone: TIMEZONE,
    timeZoneName: "short",
  })
    .formatToParts(new Date(`${iso}T02:00:00Z`))
    .find((p) => p.type === "timeZoneName");
  return part?.value || "Sydney time";
}

export const formatPrice = (sp) =>
  sp == null || !Number.isFinite(Number(sp))
    ? "—"
    : `$${Number(sp).toFixed(2)}`;

const hasTips = (race, key) =>
  Array.isArray(race?.[key]) && race[key].length > 0;

/**
 * How one edition went in a race:
 *   none — no tips for this edition
 *   top  — the Top Tip won
 *   tip  — the winner was tip 2–4 (n = 2..4)
 *   miss — none of the tips won
 */
export function editionOutcome(race, key) {
  if (!hasTips(race, key)) return { kind: "none" };
  const tips = race[key];
  if (tips[0].won) return { kind: "top" };
  const i = tips.findIndex((t) => t.won);
  return i > 0 ? { kind: "tip", n: i + 1 } : { kind: "miss" };
}

export const topTipWon = (race) =>
  EDITIONS.some(({ key }) => editionOutcome(race, key).kind === "top");

export const lexiHadWinner = (race) =>
  EDITIONS.some(({ key }) =>
    ["top", "tip"].includes(editionOutcome(race, key).kind),
  );

/** One result per race across all editions: top > tip > miss. */
export const bestOutcome = (race) =>
  topTipWon(race) ? "top" : lexiHadWinner(race) ? "tip" : "miss";

export const topTipWins = (races, key) =>
  races.filter((r) => editionOutcome(r, key).kind === "top").length;

export function stateForTrack(track, directory) {
  if (!track || !directory) return "";
  if (directory[track]?.State) return directory[track].State;
  const lower = track.toLowerCase();
  const match = Object.entries(directory).find(
    ([name, entry]) =>
      name.toLowerCase() === lower ||
      entry?.alt?.toLowerCase() === lower ||
      entry?.City?.toLowerCase() === lower,
  );
  return match?.[1]?.State || "";
}

/** Search + state + track + "show" filters. */
export function filterRaces(
  races,
  { query = "", state = "all", track = "all", show = "all" } = {},
) {
  const q = query.trim().toLowerCase();
  return races.filter((r) => {
    if (state !== "all" && r.state !== state) return false;
    if (track !== "all" && r.track !== track) return false;
    if (show === "top" && !topTipWon(r)) return false;
    if (show === "any" && !lexiHadWinner(r)) return false;
    if (!q) return true;
    if (r.track.toLowerCase().includes(q)) return true;
    if (r.winner?.name?.toLowerCase().includes(q)) return true;
    return EDITIONS.some(({ key }) =>
      (r[key] || []).some((t) => t.name?.toLowerCase().includes(q)),
    );
  });
}

export const byStartTime = (a, b) =>
  String(a.start || "").localeCompare(String(b.start || "")) ||
  a.track.localeCompare(b.track) ||
  a.race - b.race;
